import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { TextField, Box, Autocomplete, InputAdornment, CircularProgress, debounce } from '@mui/material';
import useAddressLookup from '../../lib/hooks/useAddressLookup';
import { postValidateAddress } from '../../lib/api';
import { useMediaQuery } from '@mui/material';

const sanitizeInput = (input) => {
  // Basic XSS prevention
  // console.log('input is', input)
  return input?.replace(/[<>]/g, '');
};

const AddressInput = ({
  showDeliveryInstructions = false,
  mode = 'usps',
  setNewAddress,
  setNeedUnitNumber = () => {},
}) => {
  const [value, setValue] = useState('');
  const [unitNumber, setUnitNumber] = useState('');
  const [deliveryInstructions, setDeliveryInstructions] = useState('');
  const [isValidAddressSelected, setIsValidAddressSelected] = useState(false);
  const [isAddressValidating, setIsAddressValidating] = useState(false);
  const [addressValidationFailed, setAddressValidationFailed] = useState(false);
  const [needUnitNumber, setLocalNeedUnitNumber] = useState(false);

  const { data: suggestions, refetch } = useAddressLookup(value, mode);

  // Debounced address lookup
  const debouncedRefetch = useMemo(() => debounce(() => refetch(), 300), [refetch]);

  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const validateAddress = useCallback(
    async (selectedAddress) => {
      if (!selectedAddress) {
        return;
      }

      setIsAddressValidating(true);
      setAddressValidationFailed(false);
      try {
        const fullAddress =
          selectedAddress.street_line +
          ' ' +
          selectedAddress.city +
          ' ' +
          selectedAddress.state +
          ' ' +
          selectedAddress.zipcode;
        const sanitizedAddress = sanitizeInput(fullAddress);
        const validationResponse = await postValidateAddress({ address: sanitizedAddress }, mode);
        if (!isMounted.current) return;
        if (validationResponse.message === 'Address validation incomplete.') {
          setLocalNeedUnitNumber(true);
          setNeedUnitNumber(true);
          setIsValidAddressSelected(true);
          setAddressValidationFailed(false);
        } else {
          setLocalNeedUnitNumber(false);
          setNeedUnitNumber(false);
          const isValid = validationResponse.message === 'Address is fully validated.';
          setIsValidAddressSelected(isValid);
          setAddressValidationFailed(!isValid);
        }
        const combinedAddress = {
          ...selectedAddress,
          sanitizedAddress: sanitizedAddress,
        };
        setNewAddress(combinedAddress);
      } catch (error) {
        if (!isMounted.current) return;
        console.error('Address validation failed:', error);
        setAddressValidationFailed(true);
        setLocalNeedUnitNumber(false);
        setNeedUnitNumber(false);
      } finally {
        if (!isMounted.current) return;
        setIsAddressValidating(false);
      }
    },
    [mode, setNeedUnitNumber, setNewAddress],
  );

  const handleAddressChange = useCallback(
    async (event, newValue) => {
      const sanitizedValue = sanitizeInput(newValue);
      setValue(sanitizedValue);
      setAddressValidationFailed(false); // Reset error when typing
      if (sanitizedValue.length > 5) {
        debouncedRefetch();
      }
    },
    [debouncedRefetch],
  );

  const handleSuggestionSelect = useCallback(
    (event, newValue) => {
      if (newValue) {
        const addressValue = newValue.description || newValue;
        setValue(addressValue);
        setAddressValidationFailed(false);
        validateAddress(newValue); // Pass the full object for validation
      }
    },
    [validateAddress],
  );

  // Ensure each suggestion has a unique key/id
  const getOptionKey = (option, idx) => {
    if (option.id) return option.id;
    // Fallback: combine fields or use index
    return (
      (option.description || option.street_line || '') +
      (option.city || '') +
      (option.state || '') +
      (option.zipcode || '') +
      '-' +
      idx
    );
  };

  const isMobileView = useMediaQuery('(max-width: 600px)');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <Autocomplete
        fullWidth
        options={suggestions ? suggestions : []}
        getOptionLabel={(option) => {
          const description = option.description || option.street_line || '';
          const city = option.city ? `, ${option.city}` : '';
          const state = option.state ? `, ${option.state}` : '';
          const zipcode = option.zipcode ? ` ${option.zipcode}` : '';
          return `${description}${city}${state}${zipcode}`;
        }}
        isOptionEqualToValue={(option, value) => {
          // Compare by all address fields
          return (
            (option.description || option.street_line) === (value.description || value.street_line) &&
            option.city === value.city &&
            option.state === value.state &&
            option.zipcode === value.zipcode
          );
        }}
        getOptionKey={getOptionKey}
        onInputChange={handleAddressChange}
        onChange={handleSuggestionSelect}
        inputValue={typeof value === 'string' ? value : value.description || value.street_line || ''}
        renderOption={(props, option, { index }) => (
          <li
            {...props}
            key={getOptionKey(option, index)}
          >
            {`${option.description || option.street_line || ''}${option.city ? ', ' + option.city : ''}${
              option.state ? ', ' + option.state : ''
            }${option.zipcode ? ' ' + option.zipcode : ''}`}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            label="Enter your address"
            error={addressValidationFailed && !isAddressValidating}
            {...params}
            fullWidth
            sx={{ mb: 1 }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isAddressValidating ? (
                    <CircularProgress
                      color="inherit"
                      size={20}
                    />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
              // Remove heavy custom sx, use default or minimal styling
            }}
          />
        )}
      />

      {needUnitNumber && (
        <TextField
          label="Unit Number"
          value={unitNumber}
          onChange={(e) => setUnitNumber(e.target.value)}
          fullWidth
        />
      )}

      {isValidAddressSelected && showDeliveryInstructions && (
        <TextField
          label="Delivery Instructions"
          value={deliveryInstructions}
          onChange={(e) => setDeliveryInstructions(e.target.value)}
          fullWidth
        />
      )}
    </Box>
  );
};

export default React.memo(AddressInput);
